import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { CodaltComponent } from 'app/codalt.component';
import { Subscription } from 'rxjs';
import { CustomerProject } from '../../customer-project.interface';
import { LocalStorage } from '../../local-storage.class';
import { AuthorisationService } from '../../services/auth/authorisation.service';
import { Functionalities } from '../../services/auth/functionalities';
import { CustomerProjectService } from '../../services/customer-project/customer-project.service';
import { SearchService } from '../../services/search/search.service';
import { WorkareaService } from '../../services/workarea/workarea.service';
import { Status } from '../../status.interface';
import { Chat } from '../chat';
import { ChatService } from '../chat.service';
import { NewChatDialogComponent } from '../new-chat-dialog/new-chat-dialog.component';

@Component({
    selector: 'app-chat-overview',
    templateUrl: './chat-overview.component.html',
    styleUrls: ['./chat-overview.component.scss'],
    standalone: false,
})
export class ChatOverviewComponent extends CodaltComponent implements OnInit, OnDestroy {
    @ViewChild('projectFilterSearchTemplate', { static: true }) projectFilterSearchTemplate;

    @Output()
    openChat = new EventEmitter<Chat>();

    @Input()
    currentChat: Chat;

    chats: Chat[] = [];
    isChatsManagement = AuthorisationService.hasFunctionality(Functionalities.ChatsManagement);
    statuses: Status[];
    statusIds: number[];
    search: string;
    searchProjects: number[];
    projects: CustomerProject[];

    @ViewChild('searchInput')
    private searchInput: ElementRef;

    private pauseListener: Promise<PluginListenerHandle>;
    private resumeListener: Promise<PluginListenerHandle>;

    constructor(
        private chatService: ChatService,
        private searchService: SearchService,
        private router: Router,
        private workareaService: WorkareaService,
        private customerProjectService: CustomerProjectService,
        private ngZone: NgZone,
        private dialog: MatDialog
    ) {
        super();
    }

    ngOnInit() {
        this.customerProjectService.getProjects(true, null, null, null, true).subscribe(projects => {
            this.projects = projects;
            this.searchService.searchTemplate = this.projectFilterSearchTemplate;
        });

        if (AuthorisationService.hasFunctionality(Functionalities.ChatsManagement)) {
            this.workareaService.getAvailableStatuses('chat').then(statuses => {
                this.statuses = statuses;
                this.statusIds = this.statuses.filter(s => s.isdefault === 1).map(s => s.id);
                this.getChats();
            });
        } else {
            this.getChats();
        }
        this.searchProjects = LocalStorage.getSearchProjectsIds();
        if (this.searchProjects) {
            this.changeSearch();
        }
        this.pauseListener = App.addListener('pause', () => this.ngZone.run(this.chatComponentPause));
        this.resumeListener = App.addListener('resume', () => this.ngZone.run(this.chatComponentResume));
    }

    private chatComponentPause = () => {
        setTimeout(() => {
            this.subscriptions?.unsubscribe();
            this.subscriptions = new Subscription();
        });
    };
    private chatComponentResume = () => {
        setTimeout(() => this.getChats());
    };

    openNextChat() {
        const openStatus = this.statuses.find(s => s.isdefault === 1);
        this.openChat.emit(this.chats.find(c => c.status_id === openStatus.id));
    }

    changeLoadedStatuses() {
        this.chatService.changeStatuses(this.statusIds);
    }

    changeSearch() {
        this.chatService.searchId(this.search ? this.search.trim().replace('#', '') : null, this.searchProjects);
        LocalStorage.setSearchProjectsIds(this.searchProjects);
    }

    @HostListener('window:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        if (event.getModifierState && event.getModifierState('Control') && event.key === 'f') {
            this.searchInput.nativeElement.focus();
            event.preventDefault();
        }
    }

    newChat() {
        const dialogRef = this.dialog.open(NewChatDialogComponent, {
            maxHeight: '98vh',
            maxWidth: '100vw',
            minWidth: '400px',
            width: '400px',
            panelClass: ['confirm-dialog', 'responsive-dialog'],
        });
        const ref = dialogRef.afterClosed().subscribe(chat => {
            this.router.navigate(['/', this.Routenames.chat, chat?.id]);
            ref.unsubscribe();
        });
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.searchService.searchTemplate = null;
        this.pauseListener?.then(listener => listener.remove());
        this.resumeListener?.then(listener => listener.remove());
    }

    private getChats() {
        this.subscriptions.add(
            this.chatService.getList(this.statusIds).subscribe(chats => {
                this.chats = chats.sort();
                this.watchRouteParams(this.RouteParamnames.chatId, ({ chatId }) => {
                    this.openChat.emit(this.chats.find(c => c.id === +chatId));
                });
            })
        );
    }
}
